import AchievementListItem from '@/components/Achievements/AchievementListItem';
import AudioPlayer from '@/components/AudioPlayer/AudioPlayer';
import Collection from '@/components/Collections/Collection/Collection';
import ExpandableImage from '@/components/ExpandableImage/ExpandableImage';
import Feedback from '@/components/Feedback/Feedback';
import BinocularsIcon from '@/components/Icon/BinocularsIcon';
import HeartIcon from '@/components/Icon/HeartIcon';
import InfoIcon from '@/components/Icon/InfoIcon';
import QuestionMarkIcon from '@/components/Icon/QuestionMarkIcon';
import ShapesIcon from '@/components/Icon/ShapesIcon';
import { LargeSectionBackground } from '@/components/SectionBackground/SectionBackground';
import useCollections from '@/hooks/collections/useCollections';
import useAchievements from '@/hooks/useAchievements';
import {
	MediaAttributes,
	SpeciesAttributes,
	SpeciesDescription,
	StrapiEntry,
} from '@/lib/apiTypes';
import cn from '@/lib/cs';
import { useMemo } from 'react';

export interface InformationSection {
	key: Exclude<keyof SpeciesDescription, 'id'>;
	displayName: string;
	iconComponent: React.FC<React.SVGProps<SVGSVGElement>>;
}

const informationSections: InformationSection[] = [
	{
		key: 'habitatAndTimeOfYear',
		displayName: 'Waar en wanneer',
		iconComponent: BinocularsIcon,
	},
	{
		key: 'appearance',
		displayName: 'Uiterlijk',
		iconComponent: ShapesIcon,
	},
	{
		key: 'behavior',
		displayName: 'Gedrag',
		iconComponent: HeartIcon,
	},
	{
		key: 'additionalInformation',
		displayName: 'Meer weten',
		iconComponent: QuestionMarkIcon,
	},
] as const;

const SpeciesDetails = ({
	species,
	isSpeciesInCalendar,
}: {
	species: StrapiEntry<SpeciesAttributes>;
	isSpeciesInCalendar: boolean;
}) => {
	const { findCollectionsOfSpeciesId } = useCollections();

	const collections = useMemo(
		() => findCollectionsOfSpeciesId(species.id),
		[findCollectionsOfSpeciesId, species.id],
	);

	const { achievements } = useAchievements(species.id);

	return (
		<div className="grid gap-2 pt-4">
			{/* If species is not on the calendar, display an information message */}
			{!isSpeciesInCalendar && (
				<InformationSection
					displayName="Binnenkort!"
					section={{
						id: -1,
						description:
							'Elk seizoen komen er nieuwe soorten in de app. Je kunt de soort al wel verzamelen.',
						media: { data: [] },
					}}
					iconComponent={InfoIcon}
				/>
			)}
			{/* Display information only if species is published in calendar */}
			{/* Filter out 'meer weten?' to only show it at the bottom */}
			{isSpeciesInCalendar &&
				informationSections
					.filter(
						(section) => section.key !== 'additionalInformation',
					)
					.map((section) => (
						<InformationSection
							key={section.key}
							displayName={section.displayName}
							section={
								species?.attributes?.speciesDescription[
									section.key
								]
							}
							iconComponent={section.iconComponent}
						/>
					))}
			{collections.length > 0 && (
				<ul aria-label="Kwartetten">
					{collections.map((collection) => (
						<Collection
							key={collection.id}
							collection={collection}
						/>
					))}
				</ul>
			)}
			{achievements.length > 0 && (
				<ul aria-label="Challenges" className="grid gap-4">
					{achievements.map((achievement, index) => (
						<AchievementListItem
							key={achievement.id}
							achievement={achievement}
							index={index}
							headingElement="h2"
							isInsideDialog={false}
							isOnPageOfSpeciesId={species.id}
						/>
					))}
				</ul>
			)}
			<Feedback />
			{/* Display 'meer weten' at the bottom, if species is published in calendar */}
			{isSpeciesInCalendar &&
				informationSections
					.filter(
						(section) => section.key === 'additionalInformation',
					)
					.map((section) => (
						<InformationSection
							key={section.key}
							displayName={section.displayName}
							section={
								species?.attributes?.speciesDescription[
									section.key
								]
							}
							iconComponent={section.iconComponent}
						/>
					))}
		</div>
	);
};

const InformationSection = ({
	displayName,
	section,
	iconComponent,
	children,
}: {
	displayName: string;
	section: SpeciesDescription[(typeof informationSections)[number]['key']];
	iconComponent: InformationSection['iconComponent'];
	children?: React.ReactNode;
}) => {
	const IconComponent = iconComponent;

	// Get list of connected media
	const media = useMemo(() => {
		const media: StrapiEntry<MediaAttributes>[] = [];

		if (!section?.media?.data) return media;

		// If it's just a single item, add it to the array
		if (!Array.isArray(section.media.data) && section.media.data) {
			media.push(section.media.data);
			return media;
		}

		// If it's an array, return all the items
		return section.media.data;
	}, [section]);

	// Get list of connected images
	const images = useMemo(
		() => media.filter((item) => item.attributes.mime.startsWith('image')),
		[media],
	);

	// Get list of connected audio files
	const audio = useMemo(
		() => media.filter((item) => item.attributes.mime.startsWith('audio')),
		[media],
	);

	const hasMedia = images.length > 0 || audio.length > 0;
	const hasDescription = section?.description.split(' ').length >= 2;

	if (!hasMedia && !hasDescription) return null;

	return (
		<section className="relative text-gray-50">
			<LargeSectionBackground className="text-primary" />
			<div className="relative mx-auto w-full max-w-[40rem] p-4 md:py-6">
				<h2 className="relative font-brand text-3xl first-letter:text-4xl lg:text-4xl lg:first-letter:text-5xl">
					{displayName}
					<IconComponent className="absolute end-0 top-0 size-8 rotate-2 lg:size-8" />
				</h2>
				{hasDescription && (
					<p className="select-text pb-4 text-lg font-medium leading-8 md:text-xl">
						{section.description
							.split('\n')
							.map((paragraph, index) => (
								<span key={index} className="block min-h-4">
									{paragraph}
								</span>
							))}
					</p>
				)}
				{hasMedia && (
					<ul
						aria-label="Afbeeldingen en geluiden"
						className="flex flex-wrap gap-4">
						{images.map((image, index) => (
							<li
								key={image.id}
								className={cn(
									'flex max-sm:w-full max-sm:max-w-96 max-sm:mx-auto max-sm:-px-4',
									index % 2 === 0
										? 'justify-start'
										: 'justify-end',
								)}>
								<ExpandableImage
									key={image.id}
									src={image.attributes.url}
									thumbnailSrc={
										image.attributes.formats?.thumbnail
											?.url ?? image.attributes.url
									}
									caption={image.attributes.caption}
									alt={image.attributes.alternativeText ?? ''}
									className="shrink-0"
								/>
							</li>
						))}
						{audio.map((audio) => (
							<li key={audio.id}>
								<AudioPlayer
									src={audio.attributes.url}
									mimeType={audio.attributes.mime}
									altText={
										audio.attributes.alternativeText ??
										'Geluidsbeschrijving is nog niet ingevuld door redactie'
									}
								/>
							</li>
						))}
					</ul>
				)}
				{!!children && <div className="pt-4">{children}</div>}
			</div>
		</section>
	);
};

export default SpeciesDetails;
